import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { SERVICE_CATEGORIES, SWISS_CANTONS } from '../constants/categories';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Upload, X } from 'lucide-react';
import { uploadJobPhoto } from '../services/firebaseStorage';

const JobPosting = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { API, language, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  const [jobData, setJobData] = useState({
    title: '',
    description: '',
    category: location.state?.category || '',
    budget: '',
    urgency: 'medium',
    location: { address: '', canton: '', lat: null, lng: null },
    photos: []
  });

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setJobData(prev => ({
          ...prev,
          photos: [...prev.photos, { file, preview: reader.result }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setJobData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!jobData.title || !jobData.description || !jobData.category || !jobData.budget || !jobData.location.canton) {
      toast.error(language === 'en' ? 'Please fill all required fields (including canton)' : language === 'it' ? 'Compila tutti i campi obbligatori (incluso cantone)' : 'Bitte füllen Sie alle Pflichtfelder aus (inkl. Kanton)');
      return;
    }

    setLoading(true);
    try {
      const jobResponse = await axios.post(`${API}/jobs`, { ...jobData, photos: [] });
      if (jobData.photos.length > 0) {
        const references = await Promise.all(
          jobData.photos.map((photo) => uploadJobPhoto(photo.file, user.id, jobResponse.data.id))
        );
        await axios.put(`${API}/jobs/${jobResponse.data.id}`, { photos: references });
      }
      toast.success(language === 'en' ? 'Order posted successfully!' : language === 'it' ? 'Ordine pubblicato con successo!' : 'Auftrag erfolgreich erstellt!');
      navigate('/dashboard/client');
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to post order';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (typeof errorDetail === 'object' && errorDetail?.message) {
        errorMessage = errorDetail.message;
      }
      
      toast.error(errorMessage);
      if (error.response?.status === 402) {
        navigate('/subscription-plans');
      }
    } finally {
      setLoading(false);
    }
  };

  const content = {
    en: {
      title: 'Post a New Order',
      step1: 'Service Type',
      step2: 'Order Details',
      step3: 'Location',
      step4: 'Review & Post',
      category: 'Category',
      selectCategory: 'Select a category',
      jobTitle: 'Order Title',
      description: 'Description',
      budget: 'Budget (CHF)',
      urgency: 'Urgency',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      emergency: 'Emergency',
      location: 'Location',
      address: 'Address',
      photos: 'Photos (optional)',
      uploadPhotos: 'Upload Photos',
      next: 'Next',
      back: 'Back',
      post: 'Post Order'
    },
    de: {
      title: 'Neuen Auftrag erstellen',
      step1: 'Dienstleistungstyp',
      step2: 'Auftragsdetails',
      step3: 'Standort',
      step4: 'Überprüfen & Veröffentlichen',
      category: 'Kategorie',
      selectCategory: 'Kategorie auswählen',
      jobTitle: 'Auftragstitel',
      description: 'Beschreibung',
      budget: 'Budget (CHF)',
      urgency: 'Dringlichkeit',
      low: 'Niedrig',
      medium: 'Mittel',
      high: 'Hoch',
      emergency: 'Notfall',
      location: 'Standort',
      address: 'Adresse',
      photos: 'Fotos (optional)',
      uploadPhotos: 'Fotos hochladen',
      next: 'Weiter',
      back: 'Zurück',
      post: 'Auftrag veröffentlichen'
    },
    it: {
      title: 'Pubblica un Nuovo Ordine',
      step1: 'Tipo di Servizio',
      step2: 'Dettagli Ordine',
      step3: 'Posizione',
      step4: 'Rivedi e Pubblica',
      category: 'Categoria',
      selectCategory: 'Seleziona una categoria',
      jobTitle: 'Titolo Ordine',
      description: 'Descrizione',
      budget: 'Budget (CHF)',
      urgency: 'Urgenza',
      low: 'Bassa',
      medium: 'Media',
      high: 'Alta',
      emergency: 'Emergenza',
      location: 'Posizione',
      address: 'Indirizzo',
      photos: 'Foto (opzionale)',
      uploadPhotos: 'Carica Foto',
      next: 'Avanti',
      back: 'Indietro',
      post: 'Pubblica Ordine'
    },
    fr: {
      title: 'Publier une Nouvelle Commande',
      step1: 'Type de Service',
      step2: 'Détails de la Commande',
      step3: 'Emplacement',
      step4: 'Réviser et Publier',
      category: 'Catégorie',
      selectCategory: 'Sélectionner une catégorie',
      jobTitle: 'Titre de la Commande',
      description: 'Description',
      budget: 'Budget (CHF)',
      urgency: 'Urgence',
      low: 'Faible',
      medium: 'Moyen',
      high: 'Élevé',
      emergency: 'Urgence',
      location: 'Emplacement',
      address: 'Adresse',
      photos: 'Photos (optionnel)',
      uploadPhotos: 'Télécharger des Photos',
      next: 'Suivant',
      back: 'Retour',
      post: 'Publier la Commande'
    }
  };

  const t = content[language] || content.en;

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="job-posting-page">
      <div className="max-w-3xl mx-auto px-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard/client')} 
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.back}
        </Button>

        <Card className="swiss-shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">{t.title}</CardTitle>
            {/* Progress indicator */}
            <div className="flex items-center space-x-2 mt-4">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className={`flex-1 h-2 rounded-full ${step >= s ? 'bg-blue-600' : 'bg-gray-200'}`} />
              ))}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Category */}
            {step === 1 && (
              <div className="space-y-4" data-testid="step-1">
                <h3 className="font-bold text-lg">{t.step1}</h3>
                <div>
                  <Label htmlFor="category">{t.category} *</Label>
                  <Select value={jobData.category} onValueChange={(val) => setJobData({ ...jobData, category: val })}>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder={t.selectCategory} />
                    </SelectTrigger>
                    <SelectContent data-testid="category-select-content">
                      {SERVICE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value} data-testid={`category-option-${cat.value}`}>
                          {language === 'en' ? cat.en : language === 'it' ? cat.it : cat.de}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => setStep(2)} disabled={!jobData.category} className="w-full" data-testid="next-step-1">
                  {t.next}
                </Button>
              </div>
            )}

            {/* Step 2: Details */}
            {step === 2 && (
              <div className="space-y-4" data-testid="step-2">
                <h3 className="font-bold text-lg">{t.step2}</h3>
                <div>
                  <Label htmlFor="title">{t.jobTitle} *</Label>
                  <Input
                    id="title"
                    value={jobData.title}
                    onChange={(e) => setJobData({ ...jobData, title: e.target.value })}
                    placeholder={language === 'en' ? 'e.g., Fix kitchen sink leak' : language === 'it' ? 'es., Riparare perdita lavandino' : 'z.B. Küchenspüle reparieren'}
                    data-testid="job-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="description">{t.description} *</Label>
                  <Textarea
                    id="description"
                    value={jobData.description}
                    onChange={(e) => setJobData({ ...jobData, description: e.target.value })}
                    rows={4}
                    data-testid="job-description-input"
                  />
                </div>
                <div>
                  <Label htmlFor="budget">{t.budget} *</Label>
                  <Input
                    id="budget"
                    type="number"
                    value={jobData.budget}
                    onChange={(e) => setJobData({ ...jobData, budget: e.target.value })}
                    data-testid="job-budget-input"
                  />
                </div>
                <div>
                  <Label htmlFor="urgency">{t.urgency}</Label>
                  <Select value={jobData.urgency} onValueChange={(val) => setJobData({ ...jobData, urgency: val })}>
                    <SelectTrigger data-testid="urgency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent data-testid="urgency-select-content">
                      <SelectItem value="low" data-testid="urgency-option-low">{t.low}</SelectItem>
                      <SelectItem value="medium" data-testid="urgency-option-medium">{t.medium}</SelectItem>
                      <SelectItem value="high" data-testid="urgency-option-high">{t.high}</SelectItem>
                      <SelectItem value="emergency" data-testid="urgency-option-emergency">{t.emergency}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="back-step-2">{t.back}</Button>
                  <Button onClick={() => setStep(3)} className="flex-1" data-testid="next-step-2">{t.next}</Button>
                </div>
              </div>
            )}

            {/* Step 3: Location */}
            {step === 3 && (
              <div className="space-y-4" data-testid="step-3">
                <h3 className="font-bold text-lg">{t.step3}</h3>
                <div>
                  <Label htmlFor="canton">{language === 'en' ? 'Canton *' : language === 'it' ? 'Cantone *' : 'Kanton *'}</Label>
                  <Select 
                    value={jobData.location.canton} 
                    onValueChange={(val) => setJobData({ ...jobData, location: { ...jobData.location, canton: val } })}
                  >
                    <SelectTrigger data-testid="canton-select">
                      <SelectValue placeholder={language === 'en' ? 'Select canton' : language === 'it' ? 'Seleziona cantone' : 'Kanton wählen'} />
                    </SelectTrigger>
                    <SelectContent data-testid="canton-select-content">
                      {SWISS_CANTONS.map((canton) => (
                        <SelectItem key={canton.value} value={canton.value} data-testid={`canton-option-${canton.value}`}>
                          {language === 'en' ? canton.en : language === 'it' ? canton.it : canton.de}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="address">{t.address}</Label>
                  <Input
                    id="address"
                    value={jobData.location.address}
                    onChange={(e) => setJobData({ ...jobData, location: { ...jobData.location, address: e.target.value } })}
                    placeholder={language === 'en' ? 'Enter city or specific address (optional)' : language === 'it' ? 'Inserisci città o indirizzo specifico (opzionale)' : 'Stadt oder genaue Adresse (optional)'}
                    data-testid="job-address-input"
                  />
                </div>
                <div>
                  <Label>{t.photos}</Label>
                  <div className="mt-2">
                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-600 transition-colors">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">{t.uploadPhotos}</p>
                      </div>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={handlePhotoUpload}
                        data-testid="photo-upload-input"
                      />
                    </label>
                  </div>
                  {jobData.photos.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-4">
                      {jobData.photos.map((photo, idx) => (
                        <div key={idx} className="relative">
                          <img src={photo.preview} alt={`upload-${idx}`} className="w-full h-24 object-cover rounded" />
                          <button
                            onClick={() => removePhoto(idx)}
                            className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(2)} className="flex-1" data-testid="back-step-3">{t.back}</Button>
                  <Button onClick={() => setStep(4)} className="flex-1" data-testid="next-step-3">{t.next}</Button>
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <div className="space-y-4" data-testid="step-4">
                <h3 className="font-bold text-lg">{t.step4}</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">{t.category}</p>
                    <p className="font-medium">{SERVICE_CATEGORIES.find(c => c.value === jobData.category)?.[language === 'en' ? 'en' : language === 'it' ? 'it' : 'de']}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.jobTitle}</p>
                    <p className="font-medium">{jobData.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.description}</p>
                    <p className="font-medium">{jobData.description}</p>
                  </div>
                  <div className="flex justify-between">
                    <div>
                      <p className="text-sm text-gray-500">{t.budget}</p>
                      <p className="font-bold text-blue-600">CHF {jobData.budget}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t.urgency}</p>
                      <p className="font-medium">{jobData.urgency}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(3)} className="flex-1" data-testid="back-step-4">{t.back}</Button>
                  <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="submit-job-btn">
                    {loading ? '...' : t.post}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JobPosting;